/**
 * draft — per-chat composer draft persistence backed by localStorage.
 *
 * Key shape: `atmos.draft.<activeId>`. Empty drafts are removed so the
 * storage doesn't grow forever across throwaway "new" sessions.
 *
 * Caller wires up a 250ms-debounced save on every textarea `input`,
 * a load on chat-switch, and a clear on submit.
 */

const PREFIX = "atmos.draft.";

function safeStorage(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}

export function loadDraft(activeId: string): string {
  if (!activeId) return "";
  const s = safeStorage();
  if (!s) return "";
  try { return s.getItem(PREFIX + activeId) ?? ""; }
  catch { return ""; }
}

export function saveDraft(activeId: string, text: string): void {
  if (!activeId) return;
  const s = safeStorage();
  if (!s) return;
  try {
    if (text) s.setItem(PREFIX + activeId, text);
    else s.removeItem(PREFIX + activeId);
  } catch { /* quota / private mode — swallow */ }
}

export function clearDraft(activeId: string): void {
  if (!activeId) return;
  const s = safeStorage();
  if (!s) return;
  try { s.removeItem(PREFIX + activeId); } catch { /* swallow */ }
}

/**
 * debounce — generic, returns a wrapped fn + a flush helper. We expose
 * `flush` so the composer can force a write on blur or hide.
 */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number,
): { call: (...args: A) => void; flush: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: A | null = null;
  function call(...args: A): void {
    pending = args;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      const p = pending;
      pending = null;
      if (p) fn(...p);
    }, ms);
  }
  function flush(): void {
    if (timer) { clearTimeout(timer); timer = null; }
    const p = pending;
    pending = null;
    if (p) fn(...p);
  }
  return { call, flush };
}
